/**
 * Schedule tab — automatic payroll runs. Rows in payroll_schedules are fired
 * by payrollScheduleDispatcher (5-min cron) through the same dry-run →
 * clean-gate → live-run pipeline humans use; results land in the Runs tab
 * (schedule badge) and in email.
 *
 * Master switches surfaced in the banner: schedule_enabled (arms the
 * dispatcher; Config tab) and live_enabled (required for auto_live to reach
 * the live stage). Mutations are SuperAdmin-only, matching the callable.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { payrollApi } from '../api';
import ScheduleModal from './ScheduleModal';
import scheduleMath from '../../../../functions/Payroll/schedule/scheduleMath';

const { describeCadence } = scheduleMath;

const MODE_COLORS = { dry_only: 'teal', auto_live: 'orange' };
const LAST_STATUS = {
  dry_ok: { color: 'teal', label: 'dry ok' },
  live_ok: { color: 'green', label: 'live ok' },
  held: { color: 'yellow', label: 'held' },
  skipped_empty: { color: 'gray', label: 'empty' },
  failed: { color: 'red', label: 'failed' },
};

const fmtET = (iso) => {
  if (!iso) return '—';
  return `${new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })} ET`;
};

export default function ScheduleTab({ isSuperAdmin, onOpenRun }) {
  const toast = useToast();
  const [schedules, setSchedules] = useState(null);
  const [config, setConfig] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [modal, setModal] = useState(null); // { schedule: row|null }
  const [busyId, setBusyId] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null); // { kind: 'run_now'|'delete', schedule }

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [schedRes, cfgRes] = await Promise.all([
        payrollApi.manageSchedules({ action: 'list' }),
        payrollApi.getConfig(),
      ]);
      setSchedules(schedRes.schedules || []);
      setConfig((cfgRes.current && cfgRes.current.config) || {});
    } catch (err) {
      setLoadError(err.message || 'Failed to load schedules');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleEnabled = async (s) => {
    setBusyId(s.id);
    try {
      await payrollApi.manageSchedules({ action: 'update', id: s.id, payload: { enabled: !s.enabled } });
      toast({ title: s.enabled ? 'Schedule paused' : 'Schedule enabled', status: 'success', duration: 3000 });
      await load();
    } catch (err) {
      toast({ title: 'Update failed', description: err.message, status: 'error', duration: 8000, isClosable: true });
    } finally {
      setBusyId(null);
    }
  };

  const confirmAction = async () => {
    const { kind, schedule } = confirmTarget;
    setConfirmTarget(null);
    setBusyId(schedule.id);
    try {
      if (kind === 'delete') {
        await payrollApi.manageSchedules({ action: 'delete', id: schedule.id });
        toast({ title: 'Schedule deleted', status: 'success', duration: 3000 });
        await load();
      } else {
        const { outcome } = await payrollApi.manageSchedules({ action: 'run_now', id: schedule.id });
        const runId = outcome.liveRunId || outcome.dryRunId || null;
        const messages = {
          dry_ok: 'Dry run complete — report emailed. Nothing written.',
          live_ok: 'LIVE run complete — success email sent.',
          held: 'HELD — clean-gate blockers found; HOLD email sent. Nothing written.',
          skipped_empty: 'No unprocessed rows — nothing to pay.',
          busy_retry: 'Live lock busy — the schedule will retry shortly.',
          failed: 'Run FAILED — check the email / Runs tab.',
        };
        toast({
          title: `Run now: ${outcome.status}`,
          description: messages[outcome.status] || JSON.stringify(outcome),
          status: outcome.status === 'failed' ? 'error' : outcome.status === 'held' ? 'warning' : 'success',
          duration: 10000,
          isClosable: true,
        });
        await load();
        if (runId && onOpenRun) onOpenRun(runId);
      }
    } catch (err) {
      toast({ title: kind === 'delete' ? 'Delete failed' : 'Run now failed', description: err.message, status: 'error', duration: null, isClosable: true });
      await load();
    } finally {
      setBusyId(null);
    }
  };

  if (loadError) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />{loadError}
        <Button ml={3} size="sm" onClick={load}>Retry</Button>
      </Alert>
    );
  }
  if (!schedules) return <Flex justify="center" py={10}><Spinner /></Flex>;

  const scheduleEnabled = !!(config && config.schedule_enabled === true);
  const liveEnabled = !!(config && config.live_enabled === true);
  const hasAutoLive = schedules.some((s) => s.enabled && s.run_mode === 'auto_live');

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={3} gap={2} wrap="wrap">
        <Text fontSize="sm" color="gray.500">
          Automatic runs fire on a 5-minute dispatcher (ET). Every execution is a normal run —
          it shows in the Runs tab with a <Badge variant="outline" colorScheme="cyan">scheduled</Badge> badge.
        </Text>
        <Button size="sm" colorScheme="purple" onClick={() => setModal({ schedule: null })}
          isDisabled={!isSuperAdmin} title={isSuperAdmin ? undefined : 'SuperAdmin only'}>
          New schedule
        </Button>
      </Flex>

      {!scheduleEnabled && (
        <Alert status="warning" borderRadius="md" mb={3} fontSize="sm">
          <AlertIcon />
          <Box>
            <b>The dispatcher is OFF</b> — <code>schedule_enabled</code> is not true in the engine
            config (Config tab, SuperAdmin). Schedules below will NOT fire automatically;
            &quot;Run now&quot; still works for testing.
          </Box>
        </Alert>
      )}
      {scheduleEnabled && (
        <Alert status="success" borderRadius="md" mb={3} fontSize="sm">
          <AlertIcon />
          Dispatcher armed — due schedules fire within 5 minutes of their slot.
        </Alert>
      )}
      {hasAutoLive && !liveEnabled && (
        <Alert status="warning" borderRadius="md" mb={3} fontSize="sm">
          <AlertIcon />
          An <b>auto_live</b> schedule is enabled but <code>live_enabled</code> is false — the live
          stage will FAIL (and auto-pause the schedule). Flip <code>live_enabled</code> in Config
          before its next slot, or switch the schedule to dry_only.
        </Alert>
      )}

      <Box overflowX="auto" borderWidth="1px" borderRadius="md">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Label</Th>
              <Th>Cadence</Th>
              <Th>Next fire</Th>
              <Th>Mode</Th>
              <Th>Last result</Th>
              <Th>Enabled</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {schedules.length === 0 && (
              <Tr>
                <Td colSpan={7}>
                  <Text py={4} textAlign="center" color="gray.500">
                    No schedules yet — try &quot;New schedule&quot; with dry_only mode first
                    (report email, zero risk).
                  </Text>
                </Td>
              </Tr>
            )}
            {schedules.map((s) => {
              const last = LAST_STATUS[s.last_status] || null;
              return (
                <Tr key={s.id} opacity={s.enabled ? 1 : 0.55}>
                  <Td>
                    <Text fontWeight="semibold" fontSize="sm">{s.label}</Text>
                    {s.notes && <Text fontSize="xs" color="gray.500" maxW="220px" isTruncated title={s.notes}>{s.notes}</Text>}
                  </Td>
                  <Td><Text fontSize="xs">{describeCadence(s)}</Text></Td>
                  <Td whiteSpace="nowrap"><Text fontSize="xs">{s.enabled ? fmtET(s.next_run_at) : 'paused'}</Text></Td>
                  <Td><Badge colorScheme={MODE_COLORS[s.run_mode] || 'gray'}>{s.run_mode}</Badge></Td>
                  <Td whiteSpace="nowrap">
                    {last ? (
                      <>
                        <Badge colorScheme={last.color}>{last.label}</Badge>
                        <Text as="span" fontSize="xs" color="gray.500" ml={2}>{fmtET(s.last_fired_at)}</Text>
                        {s.last_run_id && onOpenRun && (
                          <Button size="xs" variant="link" colorScheme="blue" ml={2}
                            onClick={() => onOpenRun(s.last_run_id)}>
                            view run
                          </Button>
                        )}
                        {s.consecutive_failures > 0 && (
                          <Badge ml={2} colorScheme="red" variant="outline">{s.consecutive_failures}× fail</Badge>
                        )}
                      </>
                    ) : <Text fontSize="xs" color="gray.500">never fired</Text>}
                  </Td>
                  <Td>
                    <Switch size="sm" isChecked={s.enabled} isDisabled={!isSuperAdmin || busyId === s.id}
                      onChange={() => toggleEnabled(s)} />
                  </Td>
                  <Td whiteSpace="nowrap">
                    <Button size="xs" variant="outline" mr={2} isDisabled={!isSuperAdmin || !!busyId}
                      isLoading={busyId === s.id}
                      onClick={() => setConfirmTarget({ kind: 'run_now', schedule: s })}>
                      Run now
                    </Button>
                    <Button size="xs" variant="ghost" mr={1} isDisabled={!isSuperAdmin || !!busyId}
                      onClick={() => setModal({ schedule: s })}>
                      Edit
                    </Button>
                    <Button size="xs" variant="ghost" colorScheme="red" isDisabled={!isSuperAdmin || !!busyId}
                      onClick={() => setConfirmTarget({ kind: 'delete', schedule: s })}>
                      Delete
                    </Button>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      <ScheduleModal
        isOpen={!!modal}
        onClose={() => setModal(null)}
        schedule={modal ? modal.schedule : null}
        onSaved={load}
      />

      <Modal isOpen={!!confirmTarget} onClose={() => setConfirmTarget(null)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {confirmTarget && confirmTarget.kind === 'delete' ? 'Delete schedule?' : 'Run this schedule now?'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {confirmTarget && confirmTarget.kind === 'delete' && (
              <Text fontSize="sm">
                Delete <b>{confirmTarget.schedule.label}</b>? Past runs keep their history;
                this only removes the schedule. (Audited.)
              </Text>
            )}
            {confirmTarget && confirmTarget.kind === 'run_now' && (
              <>
                <Text fontSize="sm" mb={3}>
                  Executes <b>{confirmTarget.schedule.label}</b> immediately through the exact
                  scheduled flow (emails included), regardless of its next slot.
                </Text>
                {confirmTarget.schedule.run_mode === 'auto_live' ? (
                  <Alert status="warning" borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    This schedule is <b>auto_live</b>: if the clean-gate passes, it will run REAL
                    payroll — pipeline stamps + workbook rows. All live interlocks still apply.
                  </Alert>
                ) : (
                  <Alert status="info" borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    dry_only — computes and emails a report. Nothing is written to the sheets.
                  </Alert>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => setConfirmTarget(null)}>Cancel</Button>
            <Button
              colorScheme={confirmTarget && confirmTarget.kind === 'delete' ? 'red'
                : confirmTarget && confirmTarget.schedule.run_mode === 'auto_live' ? 'orange' : 'teal'}
              onClick={confirmAction}
            >
              {confirmTarget && confirmTarget.kind === 'delete' ? 'Delete' : 'Run now'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
